/*
 *
 * Behavior on touch devices (pointer: coarse):
 *   1st tap on a product card  → class `is-touched` added, slideshow advances
 *                                 to second image. The tap does NOT navigate.
 *   2nd tap on the active card → navigation proceeds normally (default click).
 *   Tap on a different card    → previous card resets (image rewinds, class
 *                                 removed); new card becomes active.
 *   Tap outside any card       → active card resets.
 *
 * Listens at document level in capture phase so this handler runs BEFORE
 * <product-card>'s own bubble-phase click handler that navigates to PDP.
 */
(() => {
  if (typeof window === 'undefined') return;
  if (!window.matchMedia('(pointer: coarse)').matches) return;

  /** @type {HTMLElement | null} */
  let activeCard = null;
  let suppressNextClick = false;

  /** @param {HTMLElement} card */
  const getSlideshow = (card) => card.querySelector('slideshow-component');

  /** @param {HTMLElement | null} card */
  const deactivate = (card) => {
    if (!card) return;
    card.classList.remove('is-touched');
    const slideshow = getSlideshow(card);
    if (slideshow && typeof slideshow.previous === 'function') {
      slideshow.previous(undefined, { animate: false });
    }
  };

  /** @param {HTMLElement} card */
  const activate = (card) => {
    card.classList.add('is-touched');
    const slideshow = getSlideshow(card);
    if (slideshow && typeof slideshow.next === 'function') {
      slideshow.next(undefined, { animate: false });
    }
  };

  document.addEventListener(
    'touchstart',
    (e) => {
      if (!(e.target instanceof Element)) return;
      const card = /** @type {HTMLElement | null} */ (e.target.closest('product-card'));

      if (!card) {
        if (activeCard) {
          deactivate(activeCard);
          activeCard = null;
          suppressNextClick = false;
        }
        return;
      }

      if (card === activeCard) {
        // Re-tapping the already-active card — allow this tap's click to navigate.
        suppressNextClick = false;
        return;
      }

      if (activeCard) deactivate(activeCard);
      activate(card);
      activeCard = card;
      suppressNextClick = true;
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'click',
    (e) => {
      if (!suppressNextClick) return;
      if (!(e.target instanceof Element)) return;
      const card = e.target.closest('product-card');
      if (card && card === activeCard) {
        e.preventDefault();
        e.stopPropagation();
        suppressNextClick = false;
      }
    },
    { capture: true }
  );
})();
